import {PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import type {PullRequestPickerPullRequest} from '@github-ui/item-picker/PullRequestAndBranchPicker'
import {getPrState} from '@github-ui/item-picker/PullRequestAndBranchPicker'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {TagIcon} from '@primer/octicons-react'
import {ActionList, Label} from '@primer/react'
import {clsx} from 'clsx'
import {graphql} from 'react-relay'

import type {PullRequestRelease} from './DevelopmentSection'
import styles from './LinkedPullRequests.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const LinkedPullRequestsReleaseFragment = graphql`
  fragment LinkedPullRequestsRelease on Release @inline {
    id
    tagName
    name
    url
    isPrerelease
    isLatest
  }
`

type LinkedPullRequestsProps = {
  linkedPullRequests: PullRequestPickerPullRequest[]
  releaseByPrId?: Map<string, PullRequestRelease>
}

export function LinkedPullRequests({linkedPullRequests, releaseByPrId}: LinkedPullRequestsProps) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  return (
    <>
      {linkedPullRequests.map(pullRequest => {
        const release = releaseByPrId?.get(pullRequest.id)

        if (release) {
          return (
            <li key={pullRequest.id} className={styles.CustomItem}>
              <a href={pullRequest.url} target="_blank" rel="noopener noreferrer" className={styles.PrLink}>
                <span className={styles.LeadingVisual}>{getPrIcon(pullRequest)}</span>
                <span className={styles.PrContent}>
                  <span className={styles.PrTitle}>{pullRequest.title}</span>
                  {pullRequest.repository?.nameWithOwner && (
                    <span className={styles.PrRepo}>{pullRequest.repository.nameWithOwner}</span>
                  )}
                </span>
              </a>
              <a
                href={release.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.ReleaseLink}
                onClick={() =>
                  sendClickAnalyticsEvent({
                    category: 'ISSUE_DEVELOPMENT_SECTION',
                    action: 'click.release_link',
                    label: `ref_cta:release_tag;is_latest:${release.isLatest};is_prerelease:${release.isPrerelease};`,
                  })
                }
              >
                <span className="sr-only">Release</span>
                <TagIcon
                  size={16}
                  className={clsx(
                    styles.ReleaseIcon,
                    release.isLatest ? styles.ReleaseIconLatest : styles.ReleaseIconDefault,
                  )}
                />
                <span className={styles.ReleaseTagName}>{release.tagName}</span>
                {release.isLatest && (
                  <Label variant="success" size="small">
                    Latest
                  </Label>
                )}
                {release.isPrerelease && (
                  <Label variant="attention" size="small">
                    Pre-release
                  </Label>
                )}
              </a>
            </li>
          )
        }

        return (
          <ActionList.LinkItem
            href={pullRequest.url}
            target="_blank"
            key={pullRequest.id}
            className={styles.ActionList_LinkItem}
          >
            <ActionList.LeadingVisual>{getPrIcon(pullRequest)}</ActionList.LeadingVisual>
            {pullRequest.title}
            {pullRequest.repository?.nameWithOwner && (
              <ActionList.Description variant="block">{pullRequest.repository.nameWithOwner}</ActionList.Description>
            )}
          </ActionList.LinkItem>
        )
      })}
    </>
  )
}

function getPrIcon(pullRequest: PullRequestPickerPullRequest) {
  const prState = getPrState(
    pullRequest.isDraft && pullRequest.state === 'OPEN',
    pullRequest.isInMergeQueue,
    pullRequest.state,
  )
  const Icon = PullRequestStateIcons[prState]
  return <Icon />
}
